import React, { createElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.css";
import ms, {
  secondaryModalState,
} from "../../../state-observables/modal/ModalState";
import { appendSerialNumberToOnboardingCookie } from "../../../utils/warranty-service/cookies";
import {
  isSerialNumberRegistered,
  isUserRegistered,
} from "../../../utils/warranty-service/warranty-service-utils";
import { FaSpinner } from "react-icons/fa";
import ReactTooltip from "react-tooltip";
import ServiceProgramInfo from "./ServiceProgramInfo";
// import FirmwareVersionChekcer from "../../../utils/FirmwareVersionChecker";
import { useNavigate } from "react-router-dom";
import UserRole from "../../oneoffs/UserRole";
import { UserRole as UserRoleType } from "../../../store/user-role-context";
import EmailAlreadyRegisteredModal from "./EmailAlreadyRegisteredModal";
import WarrantyMailer from "../../../utils/warranty-service/WarrantyMailer";
import ServiceProgramAddMoreRobots from "../ServiceProgramAddMoreRobotsModal";
import { CustomBluetoothItem } from "../../../types/userDeviceInfo";
import { toast } from "react-toastify";
import Logger from "../../../services/logger/Logger";
// import ServiceProgramAddMoreRobots from "../ServiceProgramAddMoreRobotsModal";

const SHOW_LOGS = true;
const TAG = "ServiceProgramOnboardingModal";

function ServiceProgramOnboardingModal() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [school, setSchool] = useState("");
  const [serialNumbersToBeSubmitted, setSerialNumbersToBeSubmitted] = useState<string[]>([]);
  const [isLoadingMessage, setIsLoadingMessage] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const [modalContent, setModalContent] = useState<
    "user-selector" | "onboarding"
  >("user-selector");
  const navigate = useNavigate();

  useEffect(() => {
    // set onboarding cookie
    if (modalContent === "onboarding") {
      const connectedRaftsSns = getSnsFromConnectedRafts();
      for (const raftSn of connectedRaftsSns) {
        if (raftSn) {
          appendSerialNumberToOnboardingCookie(raftSn);
        }
      }
      ms.modalTitle = t('service_program.title');
    }
  }, [modalContent, t]);

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // validation
    const typedEmail = e.target.value;
    const trimmedEmail = typedEmail.trim();
    setEmail(trimmedEmail);
  };

  const handleModalContentChange = (newModalContent: "user-selector" | "onboarding") => {
    ms.updateModalProps({ withCloseButton: false })
    setModalContent(newModalContent);
  };

  const onCancel = () => {
    ms.closeModal();
  };

  // Helper to always return a string for translations
  const safeT = (key: string, options?: any) => {
    const result = t(key, options);
    return typeof result === 'string' ? result : '';
  };

  const onAddMoreRobots = async () => {
    setIsLoadingMessage(String(safeT('service_program.scanning')));
    try {
      const devices: CustomBluetoothItem[] = await secondaryModalState.setModal(
        createElement(ServiceProgramAddMoreRobots),
        String(safeT('service_program.add_more_robots_title'))
      );
      setIsLoadingMessage(String(safeT('service_program.getting_serials')));
      setSerialNumbersToBeSubmitted([]);
      const connectedRaftsSns = getSnsFromConnectedRafts();
      for (const device of devices) {
        try {
          const serialNumber = device.serialNumber;
          const serialNumberTyped: string = serialNumber as string;
          // add serial number to list only if it's not same as any of the connected rafts sns
          const isSameAsConnectedRaft = connectedRaftsSns.some((raftSn) => raftSn === serialNumberTyped);
          if (serialNumberTyped && !isSameAsConnectedRaft) {
            // also, make sure this serial number is not already registered
            const isSerialNumberInDatabase = await isSerialNumberRegistered(
              serialNumberTyped
            );
            if (isSerialNumberInDatabase) {
              window.applicationManager.toaster.warn(
                t('service_program.serial_already_registered', { serial: serialNumberTyped }),
                { autoClose: 5000 }
              );
            } else {
              setSerialNumbersToBeSubmitted((prevSerialNumbers) => [
                ...prevSerialNumbers,
                serialNumberTyped,
              ]);
            }
          }
        } catch (error) {
          Logger.error(SHOW_LOGS, TAG, "Error:", error);
        }
      }
    } catch (error) {
      Logger.error(SHOW_LOGS, TAG, "Error:", error);
    }
    setIsLoadingMessage("");
  };

  const onSubmit = async () => {
    // submit email and school only if serial number is not already in the database
    // and only if the email is not already in the database
    if (email === "") {
      window.applicationManager.toaster.error(t('service_program.enter_email'), { autoClose: 5000 });
      return;
    }
    const connectedRaftsSns = getSnsFromConnectedRafts();
    if (serialNumbersToBeSubmitted.length < 1 && connectedRaftsSns.length < 1) {
      window.applicationManager.toaster.error(t('service_program.connect_or_add_robot'), { autoClose: 5000 });
      return;
    }
    if (!agreeToTerms) {
      window.applicationManager.toaster.error(t('service_program.must_agree_terms'), { autoClose: 5000 });
      return;
    }
    setIsLoadingMessage(String(safeT('service_program.registering')));
    setTimeout(() => setIsLoadingMessage(""), 5000); // set isLoadingMessage to empty string after 5 seconds to make sure it doesn't stay forever

    const isSerialNumberInDatabasePromises = connectedRaftsSns.map((serialNumber) =>
      isSerialNumberRegistered(serialNumber)
    );
    const isSerialNumberInDatabase = await Promise.all(isSerialNumberInDatabasePromises);

    const isEmailInDatabase = await isUserRegistered(email);
    if (isEmailInDatabase) {
      openEmailAlreadyRegisteredModal(email, [...serialNumbersToBeSubmitted, ...connectedRaftsSns]);
      setIsLoadingMessage("");
      return;
    }
    if (isSerialNumberInDatabase.some((isRegistered) => isRegistered)) {
      // get the serial number that is already registered
      for (let i = 0; i < isSerialNumberInDatabase.length; i++) {
        if (isSerialNumberInDatabase[i]) {
          const serialNumber = serialNumbersToBeSubmitted[i];
          toast.error(
            <>
              {t('service_program.serial_already_registered', { serial: serialNumber })}
            </>,
            {
              autoClose: 5000,
            }
          );
        }
      }
    }

    // send registration email
    const didSend = await WarrantyMailer.sendRegistrationConfirmationEmail(email, school, [
      ...serialNumbersToBeSubmitted,
      ...connectedRaftsSns
    ]);
    if (!didSend) {
      window.applicationManager.toaster.error(t('service_program.email_send_error'));
      setIsLoadingMessage("");
      return;
    }
    window.applicationManager.toaster.success(t('service_program.registration_email_sent', { email }), { autoClose: false });
    setIsLoadingMessage("");
    ms.closeModal(null, () => navigate("/"));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  const handleUserRoleSelection = (role: UserRoleType) => {
    if (role === "teacher") {
      handleModalContentChange("onboarding");
    } else {
      ms.closeModal();
    }
    window.applicationManager.userRoleCtx?.setUserRole(role);
  }

  const openEmailAlreadyRegisteredModal = (email: string, serialNumbers: string[]) => {
    secondaryModalState.setModal(
      createElement(EmailAlreadyRegisteredModal, { email, serialNumbers }),
      "Email Already Registered"
    );
  }

  return (
    <div className={styles.container}>
      {modalContent === "onboarding" ? (
        <>
          <h1>{t('service_program.title')}</h1>
          <ServiceProgramInfo />
          {/* <button
            className={styles.comingSoonButton}
            onClick={handleComingSoonClick}
          >
            Coming Soon: Sneak Peek
          </button> */}
          <form onSubmit={handleSubmit} className={styles.form}>
            <label htmlFor="email" className={styles.label}>
              {t('service_program.email_label')}
              <input
                type="email"
                id="email"
                className={styles.input}
                value={email}
                onChange={onEmailChange}
                required
              />
            </label>
            <label htmlFor="school" className={styles.label}>
              {t('service_program.school_label')}
              <input
                type="text"
                id="school"
                className={styles.input}
                value={school}
                onChange={(e) => setSchool(e.target.value)}
              />
              <div className={styles.termsContainer}>
                <input
                  type="checkbox"
                  id="terms"
                  className={styles.checkbox}
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  required
                />
                {" "}
                <label htmlFor="terms" className={styles.termsLabel}>
                  {t('service_program.agree_terms_prefix')}
                  <a
                    href="https://marty-webapp.web.app/warranty-terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('service_program.warranty_terms')}
                  </a>
                  .
                </label>
              </div>
              <div className={styles.afterRegistrationInfo}>
                <p className={styles.afterRegistrationInfoParagraph}><b>{t('service_program.after_registration_bold')}</b> {t('service_program.after_registration_info')}</p>
              </div>
            </label>
            <label htmlFor="serialNo" className={styles.label}>
              {t('service_program.serial_label')}
              {[...getSnsFromConnectedRafts(), ...serialNumbersToBeSubmitted].map((serialNumber) => (
                <input
                  key={serialNumber}
                  type="text"
                  id="serialNo"
                  className={styles.input}
                  value={serialNumber}
                  disabled
                />
              ))}
            </label>
            {isLoadingMessage && (
              <div className={styles.scanningForMoreRobots}>
                <h5>
                  {isLoadingMessage} <FaSpinner />
                </h5>
              </div>
            )}
            <div className={styles.buttonContainer}>
              <button
                type="submit"
                className={[styles.submitButton, isLoadingMessage !== "" ? styles.disabledButton : ""].join(" ")}
                disabled={isLoadingMessage !== ""}
              >
                {t('service_program.submit')}
              </button>
              <button
                type="button"
                className={[styles.cancelButton, isLoadingMessage !== "" ? styles.disabledButton : ""].join(" ")}
                onClick={onCancel}
                disabled={isLoadingMessage !== ""}
              >
                {t('service_program.cancel')}
              </button>
              <ReactTooltip id="add-more-robots-tooltip" effect="solid" />
              <button
                disabled={isLoadingMessage !== ""}
                type="button"
                className={[styles.addMoreRobotsButton, isLoadingMessage !== "" ? styles.disabledButton : ""].join(" ")}
                onClick={onAddMoreRobots}
                data-tip={t('service_program.add_more_robots_tooltip')}
                data-for={"add-more-robots-tooltip"}
              >
                {t('service_program.add_more_robots')}
              </button>
            </div>
          </form>
        </>
      ) : (
        <UserRole onUserRoleSelection={handleUserRoleSelection} isForWarrantyOnboarding={true} />
      )}
    </div>
  );
}

export default ServiceProgramOnboardingModal;


const getSnsFromConnectedRafts = () => {
  const connectedRafts = window.applicationManager.connectedRafts;
  const serialNumbers: string[] = [];
  for (const raft in connectedRafts) {
    const raftObj = connectedRafts[raft];
    if (raftObj && raftObj.getSerialNumber) {
      const serialNumber = raftObj.getSerialNumber();
      if (serialNumber) {
        serialNumbers.push(serialNumber);
      }
    }
  }
  return serialNumbers;
}